import React, { useCallback, useEffect, useState } from 'react';
import { useConfirmation } from './ConfirmationContext';
import { useAlert } from './AlertContext';
import Table from './Tables';
import Button from './Buttons';
import { formatDateTimeLocal } from '../utils/formatDate';
import { useChannelApi } from '../Services/ChannelApiProvider';
import GuardrailSettings from './GuardrailSettings';
import useTablePagination from '../Hooks/useTablePagination';

function GuardrailsList({ channelId, canEdit, loading }) {
  const { showConfirmation } = useConfirmation();
  const { showAlert } = useAlert();
  const { getGuardrails, deleteGuardrail } = useChannelApi();

  const [editingGuardrail, setEditingGuardrail] = useState(null);
  const [guardrails, setGuardrails] = useState([]);
  const [totalGuardrails, setTotalGuardrails] = useState(0);

  const tableTitle = 'Guardrails';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  const [guardrailsLoading, setGuardrailsLoading] = useState(false);

  const updateGuardrailList = () => fetchGuardrails(storedSkip(), storedLimit());

  const fetchGuardrails = useCallback(
    async (skip, limit) => {
      if (channelId && channelId !== 'new-channel') {
        setGuardrailsLoading(true);
        try {
          const responseData = await getGuardrails(channelId, skip, limit);
          setGuardrails(responseData.items || []);
          setTotalGuardrails(responseData.totalCount || 0);
        } catch (err) {
          console.error('Failed to fetch guardrails:', err);
          if (err.response && err.response.status === 404) {
            setGuardrails([]);
            setTotalGuardrails(0);
          }
        } finally {
          setGuardrailsLoading(false);
        }
      }
    },
    [channelId, getGuardrails],
  );

  useEffect(() => {
    fetchGuardrails(storedSkip(), storedLimit());
  }, [fetchGuardrails, storedSkip, storedLimit]);

  const handleTableChange = useCallback(
    (skip, limit) => {
      fetchGuardrails(skip, limit);
    },
    [fetchGuardrails],
  );

  const actionMap = {
    deactivate_forever: 'Deactivate Forever',
  };

  const columns = [
    { name: 'Name', selector: (row) => row.name, sortable: false },
    {
      name: 'Thematics',
      selector: (row) => {
        const count = row.allowed_thematics?.length || 0;
        return `${count} thematic${count !== 1 ? 's' : ''}`;
      },
      sortable: false,
    },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive'), sortable: false },
    { name: 'Updated', selector: (row) => formatDateTimeLocal(row.updated_at), sortable: false },
    { name: 'Created', selector: (row) => formatDateTimeLocal(row.created_at), sortable: false },
  ];

  const handleEditClick = (row) => {
    setEditingGuardrail(row);
  };

  const handleCreateClick = () => {
    setEditingGuardrail({
      name: '',
      action: 'deactivate_forever',
      is_active: true,
      allowed_thematics: [],
    });
  };

  const handleDelete = (selectedId) => {
    if (!selectedId || !canEdit) return;
    showConfirmation('Are you sure you want to delete this guardrail?', async () => {
      try {
        await deleteGuardrail(channelId, selectedId);
        updateGuardrailList();
      } catch (error) {
        showAlert('fail', 'Failed to delete the guardrail.');
      }
    });
  };

  return (
    <div className="radius-yes p-5 bg-yes col-12">
      {editingGuardrail ? (
        <GuardrailSettings
          channelId={channelId}
          initialData={editingGuardrail}
          canEdit={canEdit}
          onClose={() => setEditingGuardrail(null)}
          onUpdate={() => {
            setEditingGuardrail(null);
            updateGuardrailList();
          }}
        />
      ) : (
        <>
          <h2 className="mb-4">Guardrails</h2>
          <Table
            title={tableTitle}
            columns={columns}
            data={guardrails}
            onRowClicked={handleEditClick}
            onDelete={handleDelete}
            selectableRows={canEdit}
            selectableRowsHighlight
            pagination
            paginationServer
            progressPending={loading || guardrailsLoading}
            paginationTotalRows={totalGuardrails}
            paginationPerPage={initialPageSize}
            onChangePage={handleTableChange}
          />
          {canEdit && (
            <div className="d-flex justify-start" style={{ marginTop: '1rem' }}>
              <Button size="large" onClick={handleCreateClick}>
                Create New Guardrail
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default GuardrailsList;
